// Shared input plumbing for the Synth Lab pages: the held-voice manager, the
// Web MIDI hook, the computer-keyboard note map, and the persisted CC→knob
// mapping. Extracted from the v1 page so the playground, practice player and
// MIDI setup screen can all share one implementation.

import { useCallback, useEffect, useRef, useState } from "react";
import {
  PARAM_BY_ID,
  startNote,
  midiSupported,
  parseMidiMessage,
  parseControlChange,
} from "../../lib/synthLab";

// One playable octave, C4–C5. White keys carry a layout index; black keys sit
// to the right of the white key with the same index.
export const KEY_LAYOUT = [
  { midi: 60, name: "C", black: false, kb: "a" },
  { midi: 61, name: "C#", black: true, kb: "w" },
  { midi: 62, name: "D", black: false, kb: "s" },
  { midi: 63, name: "D#", black: true, kb: "e" },
  { midi: 64, name: "E", black: false, kb: "d" },
  { midi: 65, name: "F", black: false, kb: "f" },
  { midi: 66, name: "F#", black: true, kb: "t" },
  { midi: 67, name: "G", black: false, kb: "g" },
  { midi: 68, name: "G#", black: true, kb: "y" },
  { midi: 69, name: "A", black: false, kb: "h" },
  { midi: 70, name: "A#", black: true, kb: "u" },
  { midi: 71, name: "B", black: false, kb: "j" },
  { midi: 72, name: "C", black: false, kb: "k" },
];

const KB_TO_MIDI = KEY_LAYOUT.reduce((m, k) => {
  m[k.kb] = k.midi;
  return m;
}, {});

// Shared voice manager: one set of held voices driven by ANY input source —
// on-screen keys, the computer keyboard, or an external MIDI device — so they
// all sound the current `patch` and light up the same on-screen keys.
export const useSynthVoices = (patch) => {
  const voices = useRef({});
  const patchRef = useRef(patch);
  patchRef.current = patch;
  const [active, setActive] = useState(() => new Set());

  const press = useCallback((midi) => {
    if (voices.current[midi]) return;
    voices.current[midi] = startNote(patchRef.current, midi);
    setActive((s) => {
      const n = new Set(s);
      n.add(midi);
      return n;
    });
  }, []);

  const release = useCallback((midi) => {
    const v = voices.current[midi];
    if (v) {
      v.stop();
      delete voices.current[midi];
    }
    setActive((s) => {
      if (!s.has(midi)) return s;
      const n = new Set(s);
      n.delete(midi);
      return n;
    });
  }, []);

  // Computer-keyboard control (works regardless of focus on a non-text element).
  useEffect(() => {
    const down = (e) => {
      if (e.repeat || e.metaKey || e.ctrlKey || e.altKey) return;
      const tag = document.activeElement && document.activeElement.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA") return;
      const midi = KB_TO_MIDI[e.key.toLowerCase()];
      if (midi != null) press(midi);
    };
    const up = (e) => {
      const midi = KB_TO_MIDI[e.key.toLowerCase()];
      if (midi != null) release(midi);
    };
    window.addEventListener("keydown", down);
    window.addEventListener("keyup", up);
    const held = voices.current;
    return () => {
      window.removeEventListener("keydown", down);
      window.removeEventListener("keyup", up);
      Object.values(held).forEach((v) => v.stop());
      Object.keys(held).forEach((k) => delete held[k]);
    };
  }, [press, release]);

  return { press, release, active };
};

export const MIDI_SUPPORTED = midiSupported();

// Web MIDI: when `enabled`, request access and route note on/off from every
// connected input into press/release, and CC messages into `onControl`.
// Re-binds on hot-plug (statechange). Returns connection status, the device
// names, and the last note/control seen.
export const useMidiInput = (press, release, enabled, onControl) => {
  const [status, setStatus] = useState("idle"); // idle | requesting | ready | denied | unsupported
  const [inputs, setInputs] = useState([]);
  const [lastNote, setLastNote] = useState(null);
  const [lastControl, setLastControl] = useState(null); // { controller, value }
  const accessRef = useRef(null);
  // Keep the CC handler current without re-binding the MIDI access effect.
  const onControlRef = useRef(onControl);
  onControlRef.current = onControl;

  useEffect(() => {
    if (!enabled) {
      setStatus("idle");
      return undefined;
    }
    if (!MIDI_SUPPORTED) {
      setStatus("unsupported");
      return undefined;
    }
    let cancelled = false;

    const handleMessage = (e) => {
      const note = parseMidiMessage(e.data);
      if (note) {
        if (note.type === "on") {
          press(note.midi);
          setLastNote(note.midi);
        } else {
          release(note.midi);
        }
        return;
      }
      const cc = parseControlChange(e.data);
      if (cc) {
        setLastControl(cc);
        if (onControlRef.current) onControlRef.current(cc.controller, cc.value);
      }
    };

    const bind = (access) => {
      const list = [];
      access.inputs.forEach((input) => {
        input.onmidimessage = handleMessage;
        list.push(input.name || "MIDI device");
      });
      if (!cancelled) {
        setInputs(list);
        setStatus("ready");
      }
    };

    setStatus("requesting");
    navigator
      .requestMIDIAccess({ sysex: false })
      .then((access) => {
        if (cancelled) return;
        accessRef.current = access;
        bind(access);
        access.onstatechange = () => bind(access);
      })
      .catch(() => {
        if (!cancelled) setStatus("denied");
      });

    return () => {
      cancelled = true;
      const access = accessRef.current;
      if (access) {
        access.inputs.forEach((input) => {
          input.onmidimessage = null;
        });
        access.onstatechange = null;
      }
    };
  }, [enabled, press, release]);

  return { status, inputs, lastNote, lastControl };
};

/* ---------- MIDI mapping persistence ---------- */

export const CC_MAP_KEY = "synthLabCcMap"; // { paramId: ccNumber } saved per browser

export const loadCcMap = () => {
  try {
    const raw = window.localStorage.getItem(CC_MAP_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    // Keep only known params mapped to sane CC numbers.
    const clean = {};
    Object.entries(parsed).forEach(([id, cc]) => {
      if (PARAM_BY_ID[id] && Number.isInteger(cc) && cc >= 0 && cc <= 127) clean[id] = cc;
    });
    return clean;
  } catch {
    return {};
  }
};

export const saveCcMap = (map) => {
  try {
    window.localStorage.setItem(CC_MAP_KEY, JSON.stringify(map));
  } catch {
    /* ignore quota / privacy-mode errors */
  }
};
